/**
 * COVERAGE — the office's valuation election on the Estimate tab (061).
 *
 * Long distance only. Enhanced Coverage ($1.00/lb per article) is the default
 * and free; Declared Value Protection is priced. The election is the SAME
 * field the client's signed contract sets (leads.dvp_elected_at), so this
 * section also shows an election the client made, and either side's election
 * makes the contract render with DVP ticked.
 *
 * The premium is calculated from Settings unless an override is entered, which
 * needs a reason and is fixed: it is not repriced at the final scaled weight.
 *
 * ONE PREMIUM LINE. Saving keeps exactly one managed premium line on the
 * estimate (server: syncDvpLine). A hand-made valuation line already there is
 * refused with VALUATION_LINE_EXISTS, and this offers to convert it, keeping
 * its amount — never a second charge.
 */

import React, { useEffect, useState } from 'react';
import { numberInput } from '../lib/numberInput';
import { dvpQuote, dvpMoney, DVP_NAME, ENHANCED_LD_RATE_LABEL } from '../lib/dvp';
import { cardStyle, cardTitle, fieldLabel } from './TimeInputs';

export interface CoverageSave {
  coverage: 'enhanced' | 'dvp';
  declared_value?: number;
  premium_override?: number | null;
  override_reason?: string | null;
  convert_item_id?: string | null;
}

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

const input: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', background: 'var(--crm-panel-2)', border: '1px solid var(--crm-line)', borderRadius: '6px',
  color: 'var(--crm-text)', fontSize: '13px', padding: '6px 8px', fontFamily: "'DM Mono', monospace",
};
const btn = (primary: boolean): React.CSSProperties => ({
  padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '11px',
  background: primary ? 'var(--crm-accent-fill)' : 'var(--crm-panel-2)', color: primary ? 'var(--crm-on-fill)' : 'var(--crm-muted)',
  border: primary ? 'none' : '1px solid var(--crm-line)',
});

export function CoveragePanel({ lead, company, items, busy, onSave }: {
  lead: any;
  company: any;
  /** The estimate's stored rows (dvp_managed marks the premium line). */
  items: any[];
  busy: boolean;
  onSave: (payload: CoverageSave) => Promise<void>;
}) {
  const elected = !!lead.dvp_elected_at;
  const storedOverride = lead.dvp_premium_override != null && lead.dvp_premium_override !== '' ? Number(lead.dvp_premium_override) : null;

  const [choice, setChoice] = useState<'enhanced' | 'dvp'>(elected ? 'dvp' : 'enhanced');
  const [declared, setDeclared] = useState<string>(String(dvpQuote(lead, company).declaredValue));
  const [overrideOn, setOverrideOn] = useState(storedOverride != null);
  const [overrideAmt, setOverrideAmt] = useState<string>(storedOverride != null ? String(storedOverride) : '');
  const [reason, setReason] = useState<string>(lead.dvp_premium_override_reason ?? '');
  const [error, setError] = useState<string | null>(null);
  const [convert, setConvert] = useState<{ id: string; description: string; amount: number } | null>(null);

  // Back to the saved state whenever the saved lead changes.
  const savedKey = `${lead.dvp_elected_at ?? ''}|${lead.declared_value ?? ''}|${lead.dvp_premium_override ?? ''}|${lead.dvp_premium_override_reason ?? ''}|${lead.weight_lbs ?? ''}|${lead.minimum_weight ?? ''}|${company?.id ?? ''}`;
  useEffect(() => {
    setChoice(lead.dvp_elected_at ? 'dvp' : 'enhanced');
    setDeclared(String(dvpQuote(lead, company).declaredValue));
    setOverrideOn(storedOverride != null);
    setOverrideAmt(storedOverride != null ? String(storedOverride) : '');
    setReason(lead.dvp_premium_override_reason ?? '');
    setError(null);
    setConvert(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedKey]);

  const saved = dvpQuote(lead, company);
  const declaredNum = declared.trim() === '' ? saved.declaredValue : Math.max(Number(declared) || 0, saved.minimum);
  const calc = dvpQuote({ ...lead, declared_value: declaredNum, dvp_premium_override: null }, company);
  const overrideNum = overrideOn && Number(overrideAmt) > 0 ? round2(Number(overrideAmt)) : null;
  const premium = overrideNum ?? calc.premium;

  const managed = items.find((i) => i.dvp_managed);
  const managedAmount = managed ? round2(Number(managed.rate_snapshot) || 0) : null;

  const dirty =
    choice !== (elected ? 'dvp' : 'enhanced') ||
    (choice === 'dvp' && (
      declaredNum !== saved.declaredValue ||
      overrideNum !== storedOverride ||
      (overrideOn && reason.trim() !== String(lead.dvp_premium_override_reason ?? '').trim())
    ));
  // Elected on the contract (or before 061) with no managed line yet: saving adds it.
  const needsLine = choice === 'dvp' && elected && !managed;

  const save = async (convertItemId: string | null = null) => {
    setError(null);
    if (choice === 'dvp' && overrideOn) {
      if (!(overrideNum && overrideNum > 0)) { setError('Enter the override premium.'); return; }
      if (!reason.trim()) { setError('Give a reason for the override premium.'); return; }
    }
    if (choice === 'enhanced' && elected &&
        !window.confirm(`Set coverage to Enhanced? ${DVP_NAME} is removed${managed ? `, with its premium line (${dvpMoney(managedAmount)})` : ''}.`)) return;
    const payload: CoverageSave = choice === 'enhanced'
      ? { coverage: 'enhanced' }
      : {
          coverage: 'dvp',
          declared_value: declaredNum,
          premium_override: overrideNum,
          override_reason: overrideNum ? reason.trim() : null,
          convert_item_id: convertItemId,
        };
    try {
      await onSave(payload);
      setConvert(null);
    } catch (e: any) {
      const d = e?.details as any;
      if (e?.code === 'VALUATION_LINE_EXISTS' && d?.convertible && d.items?.length === 1) {
        setConvert(d.items[0]);
      } else {
        setError(e?.message || 'Coverage could not be saved.');
      }
    }
  };

  // CONVERT KEEPS THE LINE'S AMOUNT: if it is not what the premium would be,
  // it becomes the override (the reason is still the rep's to give).
  const doConvert = () => {
    if (!convert) return;
    const amount = round2(convert.amount);
    if (amount !== premium) {
      setOverrideOn(true);
      setOverrideAmt(String(amount));
      if (!reason.trim()) {
        setError(`The line's ${dvpMoney(amount)} becomes the override premium. Give a reason, then Convert again.`);
        return;
      }
      void (async () => {
        setError(null);
        try {
          await onSave({ coverage: 'dvp', declared_value: declaredNum, premium_override: amount, override_reason: reason.trim(), convert_item_id: convert.id });
          setConvert(null);
        } catch (e: any) { setError(e?.message || 'Coverage could not be saved.'); }
      })();
      return;
    }
    void save(convert.id);
  };

  const opt = (value: 'enhanced' | 'dvp', title: string, sub: string) => (
    <label style={{
      flex: '1 1 200px', display: 'flex', gap: '8px', alignItems: 'flex-start', cursor: 'pointer',
      border: `1px solid ${choice === value ? 'var(--crm-accent-line)' : 'var(--crm-line)'}`, borderRadius: '8px', padding: '8px 10px',
      background: choice === value ? 'var(--crm-accent-tint)' : 'transparent',
    }}>
      <input type="radio" name="coverage" checked={choice === value} onChange={() => { setChoice(value); setError(null); setConvert(null); }} style={{ marginTop: '2px' }} />
      <span>
        <span style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--crm-text)' }}>{title}</span>
        <span style={{ display: 'block', fontSize: '11px', color: 'var(--crm-muted)' }}>{sub}</span>
      </span>
    </label>
  );

  const electedBy = !elected ? null
    : lead.dvp_elected_by === 'office' ? 'Elected by the office'
    : 'Elected by the client on the contract';

  return (
    <div style={{ ...cardStyle, background: 'var(--crm-panel)', marginTop: '14px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
        <span style={cardTitle}>Coverage</span>
        {electedBy && (
          <span style={{ fontSize: '10px', color: 'var(--crm-green)' }}>
            {electedBy} · {new Date(lead.dvp_elected_at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' })}
          </span>
        )}
      </div>

      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        {opt('enhanced', 'Enhanced Coverage', `${ENHANCED_LD_RATE_LABEL} · included, no charge`)}
        {opt('dvp', DVP_NAME, `Priced on the declared value · ${dvpMoney(calc.deductible)} deductible`)}
      </div>

      {choice === 'dvp' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px' }}>
          <div>
            <div style={fieldLabel}>Declared value</div>
            <input
              {...numberInput(1)} style={input} placeholder={String(saved.declaredValue)}
              value={declared}
              onChange={(e) => setDeclared(e.target.value)}
            />
            <div style={{ fontSize: '10px', color: 'var(--crm-dim)', marginTop: '3px' }}>min {dvpMoney(calc.minimum)}</div>
          </div>
          <div>
            <div style={fieldLabel}>Premium</div>
            <div style={{ ...input, background: 'transparent', border: '1px solid transparent', padding: '6px 0', textDecoration: overrideNum ? 'line-through' : 'none', color: overrideNum ? 'var(--crm-dim)' : 'var(--crm-text)' }}>
              {dvpMoney(calc.premium)}
            </div>
            <div style={{ fontSize: '10px', color: 'var(--crm-dim)', marginTop: '3px' }}>
              calculated{calc.premiumAtMinimum ? ` · ${dvpMoney(calc.minPremium)} minimum` : ''} · follows the final weight
            </div>
          </div>
          <div>
            <div style={fieldLabel}>Deductible</div>
            <div style={{ ...input, background: 'transparent', border: '1px solid transparent', padding: '6px 0' }}>{dvpMoney(calc.deductible)}</div>
            <div style={{ fontSize: '10px', color: 'var(--crm-dim)', marginTop: '3px' }}>per claim · from Settings</div>
          </div>
        </div>
      )}

      {choice === 'dvp' && (
        <div>
          <label style={{ display: 'inline-flex', gap: '6px', alignItems: 'center', fontSize: '11px', color: 'var(--crm-muted)', cursor: 'pointer' }}>
            <input type="checkbox" checked={overrideOn} onChange={(e) => { setOverrideOn(e.target.checked); setError(null); }} />
            Override premium
          </label>
          {overrideOn && (
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(120px, 160px) 1fr', gap: '10px', marginTop: '6px' }}>
              <div>
                <div style={fieldLabel}>Amount</div>
                <input {...numberInput(1)} style={{ ...input, borderColor: 'var(--crm-amber-line)' }} value={overrideAmt} onChange={(e) => setOverrideAmt(e.target.value)} placeholder="0.00" />
              </div>
              <div>
                <div style={fieldLabel}>Reason (required)</div>
                <input style={{ ...input, fontFamily: 'inherit' }} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. negotiated with the client" />
              </div>
            </div>
          )}
          {overrideOn && (
            <div style={{ fontSize: '10px', color: 'var(--crm-amber)', marginTop: '4px' }}>
              A fixed premium: it is not recalculated at the final scaled weight on the invoice.
            </div>
          )}
        </div>
      )}

      {choice === 'dvp' && (
        <div style={{ fontSize: '11px', color: 'var(--crm-muted)' }}>
          Premium charged: <strong style={{ color: 'var(--crm-text)' }}>{dvpMoney(premium)}</strong>
          {managed
            ? <> · on the estimate as "{managed.description}" ({dvpMoney(managedAmount)})</>
            : <> · {elected ? 'not on the estimate yet — Save adds the premium line' : 'Save adds the premium line to the estimate'}</>}
        </div>
      )}

      {convert && (
        <div style={{ border: '1px solid var(--crm-amber-soft-line)', background: 'var(--crm-amber-tint)', borderRadius: '6px', padding: '8px 10px', fontSize: '11px', color: 'var(--crm-text)' }}>
          This estimate already charges for valuation: "{convert.description}" ({dvpMoney(convert.amount)}).
          Convert it into the {DVP_NAME} premium line? It keeps {dvpMoney(convert.amount)}
          {round2(convert.amount) !== premium ? ' as the override premium' : ''}.
          <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
            <button onClick={doConvert} disabled={busy} style={btn(true)}>Convert</button>
            <button onClick={() => setConvert(null)} disabled={busy} style={btn(false)}>Cancel</button>
          </div>
        </div>
      )}

      {error && <div role="alert" style={{ fontSize: '11px', color: 'var(--crm-red)' }}>{error}</div>}

      {(dirty || needsLine) && !convert && (
        <div style={{ display: 'flex', gap: '6px' }}>
          <button onClick={() => void save()} disabled={busy} style={btn(true)}>{busy ? 'Saving…' : needsLine && !dirty ? 'Add premium line' : 'Save coverage'}</button>
          {dirty && (
            <button
              onClick={() => { setChoice(elected ? 'dvp' : 'enhanced'); setDeclared(String(saved.declaredValue)); setOverrideOn(storedOverride != null); setOverrideAmt(storedOverride != null ? String(storedOverride) : ''); setReason(lead.dvp_premium_override_reason ?? ''); setError(null); }}
              disabled={busy} style={btn(false)}
            >
              Cancel
            </button>
          )}
        </div>
      )}
    </div>
  );
}
